// =============================================================================
// MeuDriver — Admin User Management (Batch J3 — reescrito)
// =============================================================================
// Usa RPCs get_admin_user_list, admin_set_user_status, admin_set_user_role
// via hooks useAdminUsers para centralizar lógica e invalidação de cache.
// =============================================================================

import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  Search,
  Users,
  GraduationCap,
  ChevronLeft,
  ChevronRight,
  UserX,
  UserCheck,
  UserCog,
  Eye,
  MapPin,
  Calendar,
  ShieldAlert,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { formatDate, getInitials, cn } from '@/lib/utils'
import {
  useAdminUsersList,
  useAdminBanUser,
  useAdminUnbanUser,
  useAdminChangeRole,
  type AdminUserListItem,
  type UserRole,
  type UserStatus,
} from '@/hooks/useAdminUsers'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function roleBadge(role: UserRole) {
  switch (role) {
    case 'admin':
      return <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-xs">Admin</Badge>
    case 'instructor':
      return <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">Instrutor</Badge>
    default:
      return <Badge variant="secondary" className="text-xs">Aluno</Badge>
  }
}

function statusBadge(status: UserStatus) {
  switch (status) {
    case 'banned':
      return <Badge variant="destructive" className="text-xs">Banido</Badge>
    case 'suspended':
      return <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">Suspenso</Badge>
    default:
      return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Ativo</Badge>
  }
}

type ConfirmAction =
  | { type: 'ban';    user: AdminUserListItem }
  | { type: 'unban';  user: AdminUserListItem }
  | { type: 'role';   user: AdminUserListItem; role: UserRole }

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export default function AdminUsers() {
  const [search,          setSearch]         = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [roleFilter,      setRoleFilter]     = useState('')
  const [statusFilter,    setStatusFilter]   = useState('')
  const [page,            setPage]           = useState(0)
  const [confirmAction,   setConfirmAction]  = useState<ConfirmAction | null>(null)

  // Debounce busca
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(0) }, 350)
    return () => clearTimeout(t)
  }, [search])

  // Reset page em mudança de filtro
  useEffect(() => { setPage(0) }, [roleFilter, statusFilter])

  const { data, isLoading } = useAdminUsersList({
    page,
    search:  debouncedSearch,
    role:    roleFilter,
    status:  statusFilter,
  })

  const banUser    = useAdminBanUser()
  const unbanUser  = useAdminUnbanUser()
  const changeRole = useAdminChangeRole()

  const users: AdminUserListItem[] = data?.users ?? []
  const total: number              = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / 20))

  const handleConfirm = useCallback(() => {
    if (!confirmAction) return

    if (confirmAction.type === 'ban') {
      banUser.mutate(confirmAction.user.id, {
        onSuccess: () => { toast.success(`${confirmAction.user.full_name ?? 'Usuário'} banido.`); setConfirmAction(null) },
        onError:   (err) => { toast.error(err.message); setConfirmAction(null) },
      })
    } else if (confirmAction.type === 'unban') {
      unbanUser.mutate(confirmAction.user.id, {
        onSuccess: () => { toast.success(`${confirmAction.user.full_name ?? 'Usuário'} desbanido.`); setConfirmAction(null) },
        onError:   (err) => { toast.error(err.message); setConfirmAction(null) },
      })
    } else if (confirmAction.type === 'role') {
      changeRole.mutate({ userId: confirmAction.user.id, role: confirmAction.role }, {
        onSuccess: () => { toast.success('Role atualizada.'); setConfirmAction(null) },
        onError:   (err) => { toast.error(err.message); setConfirmAction(null) },
      })
    }
  }, [confirmAction, banUser, unbanUser, changeRole])

  const isMutating = banUser.isPending || unbanUser.isPending || changeRole.isPending

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Usuários</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Gerencie alunos, instrutores e administradores da plataforma
        </p>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou e-mail..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="input-search-users"
              />
            </div>
            <Select value={roleFilter || 'all'} onValueChange={(v) => setRoleFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="sm:w-40" data-testid="select-role-filter">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as roles</SelectItem>
                <SelectItem value="student">Aluno</SelectItem>
                <SelectItem value="instructor">Instrutor</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="sm:w-40" data-testid="select-status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="active">Ativo</SelectItem>
                <SelectItem value="banned">Banido</SelectItem>
                <SelectItem value="suspended">Suspenso</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabela de usuários */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-4 w-4" />
            {isLoading ? 'Carregando...' : `${total} usuário${total !== 1 ? 's' : ''}`}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-lg" />
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="h-12 w-12 text-muted-foreground/40 mb-3" />
              <p className="text-sm text-muted-foreground">Nenhum usuário encontrado.</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center gap-4 py-4"
                >
                  <Avatar className="h-10 w-10 shrink-0">
                    <AvatarImage src={user.avatar_url ?? undefined} />
                    <AvatarFallback className="text-sm font-semibold bg-brand-100 text-brand-700">
                      {getInitials(user.full_name ?? 'U')}
                    </AvatarFallback>
                  </Avatar>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold truncate max-w-[180px]">
                        {user.full_name ?? 'Sem nome'}
                      </span>
                      {roleBadge(user.role)}
                      {statusBadge(user.status)}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      {user.email && (
                        <span className="text-xs text-muted-foreground truncate max-w-[200px]">{user.email}</span>
                      )}
                      {(user.city || user.state) && (
                        <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                          <MapPin className="h-3 w-3" />
                          {[user.city, user.state].filter(Boolean).join(', ')}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                        <Calendar className="h-3 w-3" />
                        {formatDate(user.created_at)}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {user.role === 'instructor'
                          ? `${user.booking_count_instructor} aula${user.booking_count_instructor !== 1 ? 's' : ''} (instrutor)`
                          : `${user.booking_count_student} reserva${user.booking_count_student !== 1 ? 's' : ''}`
                        }
                      </span>
                    </div>
                  </div>

                  {/* Ações */}
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      asChild
                      title="Ver detalhes"
                    >
                      <Link to={`/admin/usuarios/${user.id}`} data-testid={`btn-detail-${user.id}`}>
                        <Eye className="h-4 w-4" />
                      </Link>
                    </Button>

                    {user.status === 'banned' ? (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                        title="Desbanir"
                        onClick={() => setConfirmAction({ type: 'unban', user })}
                        data-testid={`btn-unban-${user.id}`}
                      >
                        <UserCheck className="h-4 w-4" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                        title="Banir"
                        onClick={() => setConfirmAction({ type: 'ban', user })}
                        data-testid={`btn-ban-${user.id}`}
                      >
                        <UserX className="h-4 w-4" />
                      </Button>
                    )}

                    <Select
                      value={user.role}
                      onValueChange={(v) =>
                        setConfirmAction({ type: 'role', user, role: v as UserRole })
                      }
                    >
                      <SelectTrigger
                        className={cn('h-8 w-8 p-0 border-0 bg-transparent hover:bg-muted', 'focus:ring-0')}
                        title="Mudar role"
                        data-testid={`btn-role-${user.id}`}
                      >
                        <UserCog className="h-4 w-4 mx-auto text-muted-foreground" />
                      </SelectTrigger>
                      <SelectContent align="end">
                        <SelectItem value="student">Aluno</SelectItem>
                        <SelectItem value="instructor">Instrutor</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Paginação */}
          {!isLoading && total > 0 && (
            <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
              <p className="text-sm text-muted-foreground">
                Página {page + 1} de {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={page === 0}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  data-testid="btn-prev-page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="btn-next-page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de confirmação para ações destrutivas */}
      <Dialog open={Boolean(confirmAction)} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-destructive" />
              {confirmAction?.type === 'ban'   && 'Banir usuário'}
              {confirmAction?.type === 'unban' && 'Desbanir usuário'}
              {confirmAction?.type === 'role'  && 'Alterar role'}
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.type === 'ban' && (
                <>
                  Tem certeza que deseja banir <strong>{confirmAction.user.full_name ?? 'este usuário'}</strong>?
                  O usuário perderá acesso à plataforma.
                </>
              )}
              {confirmAction?.type === 'unban' && (
                <>
                  Tem certeza que deseja desbanir <strong>{confirmAction.user.full_name ?? 'este usuário'}</strong>?
                  O acesso será restaurado.
                </>
              )}
              {confirmAction?.type === 'role' && (
                <>
                  Tem certeza que deseja mudar a role de{' '}
                  <strong>{confirmAction.user.full_name ?? 'este usuário'}</strong> para{' '}
                  <strong>{confirmAction.role}</strong>?
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)} disabled={isMutating}>
              Cancelar
            </Button>
            <Button
              variant={confirmAction?.type === 'ban' ? 'destructive' : 'default'}
              onClick={handleConfirm}
              disabled={isMutating}
              data-testid="btn-confirm-action"
            >
              {isMutating ? 'Processando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
